import { Check, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { StationRow } from './StationRow';
import { KYC_ESTACIONES, type KycEstacion, type KycSubChecks } from '../types';

/**
 * Etiqueta y motivo de cada estación, en el lenguaje del vendedor (no el del proveedor).
 *
 * 🔴 El cliente lee esto de frente en la tablet, así que ninguna estación nombra la causa ni el área
 * que la revisa — ver `copyPolicy` y el test `ninguna estación le nombra al cliente la causa`.
 */
export const KYC_STATION_COPY: Record<KycEstacion, { label: string; motivo: string }> = {
    documentReading: {
        label: 'Lectura del documento',
        motivo: 'No se pudo leer bien el documento.',
    },
    templateMatching: {
        label: 'Validez del documento',
        motivo: 'El documento no coincide con un formato válido.',
    },
    alterationDetection: {
        label: 'Integridad del documento',
        motivo: 'No se pudo confirmar la integridad del documento.',
    },
    liveness: {
        label: 'Prueba de vida',
        motivo: 'No se pudo confirmar que sea una persona real frente a la cámara.',
    },
    facematch: {
        label: 'Coincidencia del rostro',
        motivo: 'El rostro no coincide con el del documento.',
    },
    ageCheck: {
        label: 'Edad del cliente',
        motivo: 'El cliente parece ser menor de 18 años.',
    },
    watchlists: {
        label: 'Validación adicional',
        motivo: 'La cuenta quedó en revisión adicional.',
    },
    premiumAmlWatchlists: {
        label: 'Revisión adicional de la cuenta',
        motivo: 'La cuenta quedó en revisión adicional.',
    },
};

/** En la ruta de solo selfie no hay documento: la cara se compara con la foto ya registrada. */
const FACEMATCH_ROUTE_MOTIVE = 'El rostro no coincide con su foto registrada.';

/**
 * Estaciones que ejecuta cada ruta.
 *
 * 🔴 **El contrato define ocho; el flujo mexicano corre seis.** `liveness` y `watchlists` no están
 * configuradas en el flujo del proveedor, así que sus casillas quedaban en gris para siempre — y una
 * casilla que nunca se llena no dice "pendiente", dice "algo se atoró". El vendedor no tiene cómo
 * distinguirlas de una que sí va a llegar.
 *
 * ⚠️ Que no se muestren NO significa que se hagan. En particular, **este flujo no valida prueba de
 * vida**: nadie confirma que haya una persona real frente a la cámara y no la foto de otra. Es una
 * decisión de riesgo abierta, anotada en el TECH_DEBT del proyecto — no un detalle de interfaz.
 *
 * La ruta de facematch conserva `liveness` porque su flujo declara verificación biométrica y todavía
 * no hemos visto una corrida completa que lo confirme. Cuando la haya, se ajusta con el dato.
 */
const ESTACIONES_POR_RUTA: Record<'completo' | 'facematch', readonly KycEstacion[]> = {
    completo: [
        'documentReading',
        'templateMatching',
        'alterationDetection',
        'facematch',
        'ageCheck',
        'premiumAmlWatchlists',
    ],
    facematch: ['facematch', 'liveness'],
};

/**
 * Qué estaciones entran en la lista.
 *
 * Se anuncian las esperadas MÁS cualquier otra que llegue con veredicto. Así, si el flujo del
 * proveedor cambia y cierra una que no teníamos prevista, aparece sola: la lista nunca esconde un
 * resultado real y no hay que tocar código para reflejarlo.
 *
 * El orden canónico manda; el filtro solo decide qué entra.
 */
function estacionesAMostrar(
    esFacematch: boolean,
    subChecks?: KycSubChecks | null,
): readonly KycEstacion[] {
    const esperadas = ESTACIONES_POR_RUTA[esFacematch ? 'facematch' : 'completo'];
    return KYC_ESTACIONES.filter((e) => esperadas.includes(e) || subChecks?.[e] !== undefined);
}

/**
 * Las estaciones del KYC con su avance en vivo. Reemplaza a las 3 fases que se pintaban siempre en
 * gris porque el contrato no traía estado por fase — ahora sí lo trae, estación por estación.
 *
 * Cuáles se listan depende de la ruta: el KYC completo corre las ocho, el facematch solo compara la
 * cara. Ver `estacionesAMostrar`.
 *
 * Tres estados visuales, y la diferencia entre los dos últimos es deliberada:
 * - **pendiente** (gris): todavía no cerró, o no corre en este flujo.
 * - **pasó** (verde): `PASS` o `CLEAR`.
 * - **no pasó** (ÁMBAR, no rojo): `FAIL` o `HIT`.
 *
 * 🔴 **Ámbar y no rojo, a propósito.** Que una estación no pase NO frena la venta: el cliente puede
 * repetir la foto ahí mismo en el SDK, y la verificación todavía puede terminar aprobada. Pintarlo en
 * rojo haría que el vendedor cancele ventas que iban a pasar. El rojo se reserva para el veredicto
 * final (`FAILED`/`PLD`), que es el único que sí decide.
 *
 * Un `HIT` en listas tampoco es un fallo — es "que lo mire cumplimiento" — pero se muestra igual que
 * un FAIL porque para el vendedor la acción es la misma: ninguna, esperar.
 *
 * Las etiquetas son sustantivos NEUTROS ("Validez del documento") y no afirmaciones ("La INE es válida"):
 * con una tacha al lado, una afirmación se lee al revés de lo que pasó.
 *
 * a11y: `aria-live="polite"` porque la lista cambia sola durante minutos — sin eso, un lector de
 * pantalla la anuncia una vez y después calla mientras todo se mueve.
 *
 * ⚠️ Sin `role="list"` explícito: el preflight de Tailwind pone `list-style: none` y Safari/VoiceOver
 * desactiva la semántica de lista cuando eso pasa, pero `jsx-a11y/no-redundant-roles` del repo lo
 * prohíbe por redundante. Cada `li` declara su estado en texto, así que la información no se pierde
 * aunque el contenedor deje de anunciarse como lista.
 */
export function EstacionesKyc({
    subChecks,
    esFacematch = false,
}: {
    subChecks?: KycSubChecks | null;
    esFacematch?: boolean;
}) {
    return (
        <ul
            aria-live="polite"
            aria-label="Revisiones de la verificación"
            className="mx-auto max-w-md text-left"
        >
            {estacionesAMostrar(esFacematch, subChecks).map((estacion) => {
                const resultado = subChecks?.[estacion];
                const paso = resultado === 'PASS' || resultado === 'CLEAR';
                const noPaso = resultado === 'FAIL' || resultado === 'HIT';
                const { label } = KYC_STATION_COPY[estacion];
                const motivo =
                    esFacematch && estacion === 'facematch'
                        ? FACEMATCH_ROUTE_MOTIVE
                        : KYC_STATION_COPY[estacion].motivo;

                // La fila se comparte con las estaciones de la apertura de cuenta (`StationRow`)
                // para que el criterio de color no se desincronice entre las dos pantallas.
                return (
                    <StationRow
                        key={estacion}
                        label={label}
                        state={paso ? 'passed' : noPaso ? 'failed' : 'pending'}
                        detail={noPaso ? motivo : undefined}
                    />
                );
            })}
        </ul>
    );
}

/** Nombre corto de cada estación para los chips; mismo candado de copy que la lista. */
export const KYC_STATION_CHIP_LABEL: Record<KycEstacion, string> = {
    documentReading: 'Documento',
    templateMatching: 'Validez',
    alterationDetection: 'Integridad',
    liveness: 'Vida',
    facematch: 'Rostro',
    ageCheck: 'Edad',
    watchlists: 'Adicional',
    premiumAmlWatchlists: 'Adicional',
};

/** Las estaciones como chips chicos mientras el cliente verifica; ámbar si no pasó, nunca rojo. */
export function KycStationChips({
    subChecks,
    isFacematch = false,
}: {
    subChecks?: KycSubChecks | null;
    isFacematch?: boolean;
}) {
    return (
        <ul
            aria-live="polite"
            aria-label="Revisiones de la verificación"
            className="mx-auto flex max-w-md flex-wrap justify-center gap-1.5"
        >
            {estacionesAMostrar(isFacematch, subChecks).map((station) => {
                const result = subChecks?.[station];
                const passed = result === 'PASS' || result === 'CLEAR';
                const failed = result === 'FAIL' || result === 'HIT';
                return (
                    <li
                        key={station}
                        data-state={passed ? 'passed' : failed ? 'failed' : 'pending'}
                        className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px]',
                            passed && 'border-success-soft-border bg-success-soft text-success',
                            failed && 'border-warning-soft-border bg-warning-soft text-warning',
                            !passed && !failed && 'border-border text-muted-foreground',
                        )}
                    >
                        {passed && <Check className="size-3" strokeWidth={3} aria-hidden />}
                        {failed && <X className="size-3" strokeWidth={3} aria-hidden />}
                        {KYC_STATION_CHIP_LABEL[station]}
                        <span className="sr-only">
                            {' '}
                            — {passed ? 'Completado' : failed ? 'No pasó' : 'Pendiente'}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}
